import React, { useState, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { postJSON, apiBaseUrl } from "apis/apiHelpers";
import { getAuthToken } from "selectors/appSelector";
import { warehouseIdSelector } from "selectors/warehouseSelector";
import { currentUserDataSelector } from "selectors/currentUserSelector";
import { getBuyplanDetails } from "actions/buyplanDetailsActions";
import { getBuyplanTotals } from "actions/buyplanTotalsActions";
import { addNotification } from "notificationStore";
import { isAdminUser } from "utils.js";
import styles from "./QuickAddModal.module.css";

const extractProducts = (data) => {
  if (!data) return [];
  if (data.data?.pr) return data.data.pr;
  if (data.products) return data.products;
  if (Array.isArray(data)) return data;
  if (data.data && Array.isArray(data.data)) return data.data;
  if (data.pr) return data.pr;
  return [];
};

const findBpsbId = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  if (obj.bpsb_id) return obj.bpsb_id;
  if (obj.buyplan_size_break_id) return obj.buyplan_size_break_id;
  
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findBpsbId(item);
      if (found) return found;
    }
  } else {
    for (const key of Object.keys(obj)) {
      const found = findBpsbId(obj[key]);
      if (found) return found;
    }
  }
  return null;
};

const getProductId = (product) => product?.product_id || product?.id;

const getItemProductId = (item) => 
  item?.product_release?.id || item?.product_release_id || item?.product_id;

const createHeaders = (authToken, warehouseId) => ({
  "Content-Type": "application/json",
  Accept: "application/brandscope.com; version=1",
  Authorization: authToken ? `Bearer ${authToken}` : "",
  warehouse_id: warehouseId || ""
});

const MAX_QUANTITY_DIGITS = 5;

const calculateSubtotal = (product, quantity) => {
  const qty = parseInt(quantity, 10) || 0;
  const price = parseFloat(product.wholesale_price || product.effective_wholesale_price || 0);
  return (qty * price).toFixed(2);
};

const getProductName = (product) => {
  return product.product.name || "-";
};

const getProductSku = (product) =>
  product.product_sku || product.product?.sku || product.sku || product.product_code || "-";

const QuickAddModal = ({ isOpen, closeModal, releaseId, buyplanId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const apiCallsInProgress = useRef({});
  const debounceTimers = useRef({});

  const authToken = useSelector(getAuthToken);
  const warehouseId = useSelector(warehouseIdSelector);
  const currentUser = useSelector(currentUserDataSelector);
  const isAdmin = isAdminUser(currentUser.get("role"));

  const enrichProductsWithBuyplanData = useCallback(async (products) => {
    if (!buyplanId || !products.length) return products;

    try {
      const baseUrl = apiBaseUrl || "";
      const headers = createHeaders(authToken, warehouseId);
      
      const response = await fetch(
        `${baseUrl}/api/buyplans/${buyplanId}/details.json?warehouse_id=${warehouseId || ""}`,
        { method: "GET", credentials: "include", headers }
      );
      
      if (!response.ok) return products;

      const detailsData = await response.json();
      const buyplanItems = detailsData.data?.buyplan_items || detailsData.buyplan_items || [];
      
      const buyplanItemsMap = new Map();
      buyplanItems.forEach(item => {
        const itemId = getItemProductId(item);
        if (itemId) {
          buyplanItemsMap.set(String(itemId), item);
        }
      });
      
      return products.map(product => {
        const productId = getProductId(product);
        const matchingItem = buyplanItemsMap.get(String(productId));
        
        if (matchingItem) {
          return {
            ...product,
            buyplan_item: {
              ...product.buyplan_item,
              total_quantity: matchingItem.total_quantity || 0,
              bpsb_id: findBpsbId(matchingItem)
            }
          };
        }
        return product;
      });
    } catch (_) {
      return products;
    }
  }, [buyplanId, authToken, warehouseId]);

  const handleSearch = useCallback(async (searchName, searchSku) => {
    if (!releaseId) return;

    // Use provided values or fall back to state
    const searchParams = {
      name: searchName !== undefined ? searchName : name,
      sku: searchSku !== undefined ? searchSku : sku,
      quick_view: true,
      buyplan_id: buyplanId,
      warehouse_id: warehouseId
    };

    setIsLoading(true);
    setHasSearched(false);
    try {
      const response = await postJSON(
        `/api/releases/${releaseId}/products.json`,
        authToken,
        null,
        {
          payload: searchParams
        }
      );

      const data = await response.json();
      const products = extractProducts(data);

      setHasSearched(true);

      if (products.length > 0) {
        const enrichedProducts = await enrichProductsWithBuyplanData(products);
        setSearchResults(enrichedProducts);

        const initialQuantities = {};
        enrichedProducts.forEach((product, index) => {
          initialQuantities[index] = product.buyplan_item?.total_quantity || 0;
        });
        setQuantities(initialQuantities);
      } else {
        setSearchResults([]);
        setQuantities({});
      }
    } catch (_error) {
      setSearchResults([]);
      setQuantities({});
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  }, [name, sku, releaseId, authToken, buyplanId, warehouseId, enrichProductsWithBuyplanData]);

  const handleClose = useCallback(() => {
    setName("");
    setSku("");
    setSearchResults([]);
    setQuantities({});
    setHasSearched(false);
    closeModal();
  }, [closeModal]);

  const updateProductQuantity = useCallback(
    async (product, quantity, productIndex) => {
      if (!buyplanId) return;

      const baseUrl = apiBaseUrl || "";
      const headers = createHeaders(authToken, warehouseId);

      try {
        const productReleaseId = product.id || product.product_id;
        let bpsbId = product.buyplan_item?.bpsb_id || findBpsbId(product.buyplan_item);

        if (!bpsbId) {
          const addRes = await fetch(`${baseUrl}/api/buyplans/${buyplanId}/items.json`, {
            method: "POST",
            credentials: "include",
            headers,
            body: JSON.stringify({
              product_release_id: productReleaseId,
              warehouse_id: warehouseId || ""
            })
          });

          let addData = {};
          try {
            addData = await addRes.json();
          } catch (_) {
            // ignore invalid/non-json response
          }

          if (addRes.ok) {
            bpsbId = findBpsbId(addData);
          } else {
            const errText = (addData.errors && addData.errors[0]) || addData.error || "";
            const alreadyExists =
              addRes.status === 422 ||
              addRes.status === 409 ||
              addRes.status === 404 ||
              (typeof errText === "string" && /already|taken|exist/i.test(errText));
            
            if (alreadyExists) {
              bpsbId = findBpsbId(addData);
            } else {
              throw new Error(errText || `add_item failed: ${addRes.status}`);
            }
          }
        }

        // Final fallback: Fetch buyplan details and search for this specific product
        if (!bpsbId) {
          try {
            const detailsRes = await fetch(
              `${baseUrl}/api/buyplans/${buyplanId}/details.json?warehouse_id=${warehouseId || ""}`,
              { method: "GET", credentials: "include", headers }
            );
            
            if (detailsRes.ok) {
              const detailsData = await detailsRes.json();
              const items = detailsData.data?.buyplan_items || detailsData.buyplan_items || [];
              const matchingItem = items.find(item => {
                const itemProductId = getItemProductId(item);
                return String(itemProductId) === String(productReleaseId);
              });
              
              if (matchingItem) {
                bpsbId = findBpsbId(matchingItem);
              }
            }
          } catch (_) {
            // Ignore fetch error
          }
        }

        if (!bpsbId) {
          // eslint-disable-next-line no-console
          console.error("[QuickAdd] Missing bpsb_id after all attempts. Product:", product);
          throw new Error("Cannot find bpsb_id - product may not be properly added to buyplan");
        }

        const updRes = await fetch(
          `${baseUrl}/api/buyplans/${buyplanId}/size_breaks/${bpsbId}/update_quantity.json`,
          {
            method: "PUT",
            credentials: "include",
            headers,
            body: JSON.stringify({
              quantity: String(quantity),
              wholesale_price: "",
              warehouse_id: warehouseId || ""
            })
          }
        );

        if (!updRes.ok) {
          throw new Error(`update_quantity failed: ${updRes.status}`);
        }

        setSearchResults(prev => prev.map((p, idx) => {
          if (idx === productIndex) {
            return {
              ...p,
              buyplan_item: {
                ...p.buyplan_item,
                total_quantity: quantity
              }
            };
          }
          return p;
        }));

        // Fetch details synchronously to force Order Review update
        await fetch(
          `${baseUrl}/api/buyplans/${buyplanId}/details.json?warehouse_id=${warehouseId || ""}`,
          { method: "GET", credentials: "include", headers }
        );

        dispatch(getBuyplanTotals(buyplanId));
        dispatch(getBuyplanDetails(buyplanId, warehouseId, {}, 999999, 0, true));

      } catch (err) {
        addNotification({
          type: "error",
          message: err && err.message ? err.message : "Failed to update quantity"
        });
      }
    },
    [buyplanId, authToken, warehouseId, dispatch]
  );

  const handleQuantityChange = useCallback(
    (index, value) => {
      const digitsOnly = value.replace(/\D/g, "").slice(0, MAX_QUANTITY_DIGITS);
      const newValue = digitsOnly === "" ? 0 : parseInt(digitsOnly, 10);

      setQuantities((prev) => ({
        ...prev,
        [index]: digitsOnly
      }));

      if (debounceTimers.current[index]) {
        clearTimeout(debounceTimers.current[index]);
      }

      debounceTimers.current[index] = setTimeout(async () => {
        if (apiCallsInProgress.current[index]) return;

        if (buyplanId && searchResults[index]) {
          apiCallsInProgress.current[index] = true;
          try {
            await updateProductQuantity(searchResults[index], newValue, index);
          } finally {
            apiCallsInProgress.current[index] = false;
          }
        }
      }, 500);
    },
    [buyplanId, searchResults, updateProductQuantity]
  );

  const totalRows = useMemo(() => searchResults.length, [searchResults.length]);

  const renderProductRow = useCallback(
    (product, index) => (
      <tr key={index}>
        <td>
          <span className={styles.expandIcon}>›</span>
          {getProductName(product)}
        </td>
        <td>{getProductSku(product)}</td>
        <td>{product.display_wholesale_price || product.effective_display_wholesale_price || "$0.00"}</td>
        <td>{product.retail_price_formatted || "$0.00"}</td>
        <td>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={MAX_QUANTITY_DIGITS}
            className={styles.qtyInput}
            value={quantities[index] || ""}
            placeholder="0"
            disabled={isAdmin}
            onChange={(e) => handleQuantityChange(index, e.target.value)}
            aria-label={t("quickAddModal.table.headers.quantity")}
          />
        </td>
        {/* <td>{product.buyplan_item?.total_quantity || 0}</td> */}
        <td>${calculateSubtotal(product, quantities[index] || 0)}</td>
      </tr>
    ),
    [quantities, handleQuantityChange, isAdmin, t]
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      backdrop={true}
      size="lg"
      centered={true}
      className={styles.modalDialog}
    >
      <ModalHeader toggle={handleClose} className={styles.modalHeader}>
        {t("quickAddModal.title")}
      </ModalHeader>

      <ModalBody className={styles.modalBody}>
        <form 
          className={styles.quickAddForm}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch(name, sku);
          }}
        >
          <div className={styles.searchRow}>
            <div className={styles.formGroup}>
              <label htmlFor="partName" className={styles.formLabel}>
                {t("quickAddModal.labels.name")}
              </label>
              <input
                type="text"
                className={styles.formInput}
                id="partName"
                placeholder={t("quickAddModal.placeholders.enterPartName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="partSku" className={styles.formLabel}>
                {t("quickAddModal.labels.sku")}
              </label>
              <input
                type="text"
                className={styles.formInput}
                id="partSku"
                placeholder={t("quickAddModal.placeholders.enterPartSku")}
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              className={styles.searchBtn}
              disabled={isLoading}
            >
              {isLoading ? "..." : t("quickAddModal.buttons.search")}
            </button>
          </div>
        </form>

        {hasSearched && searchResults.length === 0 && !isLoading && (
          <div className={styles.noResults}>
            <p className={styles.noResultsText}>
              {t("quickAddModal.labels.noMatchingProducts")}
            </p>
          </div>
        )}

        {searchResults.length > 0 && (
          <div className={styles.resultsGrid}>
            <div className={styles.tableWrapper}>
              <table className={styles.resultsTable}>
                <thead>
                  <tr>
                    <th>{t("quickAddModal.table.headers.partDescription")}</th>
                    <th>{t("quickAddModal.table.headers.sku")}</th>
                    <th>{t("quickAddModal.table.headers.wholesalePrice")}</th>
                    <th>{t("quickAddModal.table.headers.retailPrice")}</th>
                    <th>{t("quickAddModal.table.headers.quantity")}</th>
                    {/* <th>{t("quickAddModal.table.headers.backorderQuantity")}</th> */}
                    <th>{t("quickAddModal.table.headers.subtotal")}</th>
                  </tr>
                </thead>
                <tbody>{searchResults.map(renderProductRow)}</tbody>
              </table>
            </div>
            <div className={styles.resultsFooter}>
              {t("quickAddModal.table.footer.totalRows", { count: totalRows })}
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default React.memo(QuickAddModal);
